import { FEATURES, ICounterData } from "@home-health-notify/domain";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { OrganizationContext, useTranslation } from "hhn-commons";
import * as React from "react";
import { useContext, useEffect } from "react";
import { useParams } from "react-router";
import Alert from "../../alert/Alert";
import { ChatListPanel } from "../../chats/ChatListPanel";
import TabPanel from "../../common/tabs/TabPanel";
import { ROUTES } from "../../routes";
import { UserDirectory } from "../../userDirectory/UserDirectory";
import {
  LeftPanelTabWrapper,
  StyledBadge,
  TabsContainer,
} from "./LeftPanel.style";

function a11yProps(index: number, uniqueKey: string) {
  const uniqueId = `${uniqueKey}-tabpanel-${index}`;
  return {
    id: `simple-tab-${index}`,
    "aria-controls": uniqueId,
  };
}

export interface LeftPanelTabProps {
  counters: ICounterData;
  initialTabIndex?: number;
  onTabChange?: (tabIndex: number) => void;
}

export const LeftPanelTab = ({
  counters,
  initialTabIndex,
  onTabChange,
}: LeftPanelTabProps) => {
  const { selectedOrgUser } = useContext(OrganizationContext);
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "chat" });
  const allTabs = [
    {
      index: 0,
      label: tNs("messages"),
      path: ROUTES.CHATS,
      access: true,
      dataTestId: "messages-tab",
    },
    {
      index: 1,
      label: tNs("directory"),
      path: ROUTES.DIRECTORY,
      access:
        selectedOrgUser?.hasAccess?.(FEATURES.VIEW_USER_DIRECTORY) ?? false,
      dataTestId: "directory-tab",
    },
    {
      index: 2,
      label: tNs("alert"),
      path: ROUTES.ALERT,
      access: true,
      dataTestId: "alerts-tab",
    },
  ];
  const params = useParams();
  const currentPath = params["*"]?.split("/")[0];
  const currentTab = allTabs.find(tab => currentPath === tab.path);
  const urlTab = currentTab ? currentTab.index : allTabs[0].index;
  const activeTab = initialTabIndex !== undefined ? initialTabIndex : urlTab;
  const [tabValue, setTabValue] = React.useState(activeTab);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    onTabChange?.(newValue);
  };

  //Switch back to first tab when org change
  useEffect(() => {
    setTabValue(allTabs[0].index);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.orgId]);

  // When the rail is used at narrow widths, clicking a different rail icon
  // updates initialTabIndex from the parent
  useEffect(() => {
    if (initialTabIndex !== undefined) {
      setTabValue(initialTabIndex);
    }
  }, [initialTabIndex]);

  const showCount = (tab: (typeof allTabs)[number]): number => {
    switch (tab.index) {
      case 0:
        return counters.messageCounter;
      case 2:
        return counters.alertCounter;
      default:
        return 0;
    }
  };

  return (
    <LeftPanelTabWrapper>
      <TabsContainer>
        <Tabs
          value={tabValue}
          onChange={handleChange}
          aria-label="tabs in left panel"
          variant="fullWidth"
        >
          {allTabs
            .filter(tab => tab.access)
            .map(tab => {
              const count: number = showCount(tab);
              return (
                <Tab
                  key={"main-tab" + tab.index}
                  value={tab.index}
                  data-testid={tab.dataTestId}
                  icon={
                    <StyledBadge
                      color="primary"
                      badgeContent={count}
                      max={999999}
                      count={count}
                      invisible={count <= 0}
                      data-testid="left-panel-tab-counter"
                    >
                      <div>{tab.label}</div>
                    </StyledBadge>
                  }
                  {...a11yProps(tab.index, "chat-list-")}
                />
              );
            })}
        </Tabs>
      </TabsContainer>
      <TabPanel value={tabValue} index={0} uniqueKey="chat-tabs">
        <ChatListPanel counters={counters} />
      </TabPanel>
      {selectedOrgUser?.hasAccess?.(FEATURES.VIEW_USER_DIRECTORY) && (
        <TabPanel value={tabValue} index={1} uniqueKey="chat-tabs">
          <UserDirectory />
        </TabPanel>
      )}
      <TabPanel value={tabValue} index={2} uniqueKey="chat-tabs">
        <Alert counters={counters} />
      </TabPanel>
    </LeftPanelTabWrapper>
  );
};
